import type { CalendarClient } from '@wtfalch/calendar/calendar';
import type { ChatClient } from '@wtfalch/chat';
import {
  Button,
  Dialog,
  Identity,
  initialsOf,
  Message,
  type PersonCardProps,
  Pill,
  Presence,
  ToastHost,
  useDesignLocale,
  useToast,
} from '@wtfalch/design';
import {
  type CSSProperties,
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { type RoomCallBinding, startOrJoinRoomCall } from '../calls/roomCalls.ts';
import { presenceState } from '../chat/presence.tsx';

/**
 * What a person card needs to be filled in and acted on, supplied once by the
 * app shell so a mail sender, a chat member and a calendar attendee all draw
 * the same card (agora#218). A source an agora has not turned on is `null`,
 * and the card leaves that section out rather than guessing at it.
 *
 * There is no teams source: Agora reads no people/teams data today, so the
 * card never shows teams.
 */
export type PeopleSource = {
  chat: ChatClient | null;
  calendar: CalendarClient | null;
  calls?: RoomCallBinding;
  /** The company's own mail domain (the signed-in mailbox's address domain).
   *  An email address maps to a chat colleague only under this domain; unknown
   *  means no mapping. */
  mailDomain?: string | null;
  /** The signed-in person's own mail address, so their chat id reads as it. */
  selfAddress?: string | null;
  /** The signed-in person's own display name, for where chat has none for them. */
  selfName?: string | null;
  openMail: (address: string) => void;
  openRoom: (roomId: string) => void;
};

const PeopleContext = createContext<PeopleSource | null>(null);

/** The source the nearest `PeopleProvider` gives, or `null` outside one. */
export function usePeople(): PeopleSource | null {
  return useContext(PeopleContext);
}

/** Provides the source, and a toast host so a failed Message or Call can say so. */
export function PeopleProvider({ value, children }: { value: PeopleSource; children: ReactNode }) {
  return (
    <ToastHost>
      <PeopleContext.Provider value={value}>{children}</PeopleContext.Provider>
    </ToastHost>
  );
}

/** The chat user an email address belongs to: the address's local part on the
 *  signed-in user's own homeserver (the id the issuer provisions chat users
 *  under, the same assumption `ColleagueHours` makes), only for an address on
 *  the company's own mail domain (`alice@evil.example` is not colleague
 *  `@alice`), and only when that user already shares a room with this account
 *  -- chat has no user directory to ask, and a guess at a stranger would offer
 *  a DM that cannot be created. */
export function chatUserForEmail(
  chat: ChatClient,
  email: string,
  ownDomain: string | null | undefined,
): string | null {
  const server = /^@[^:]+:(.+)$/.exec(chat.userId() ?? '')?.[1];
  const at = email.lastIndexOf('@');
  const local = email.slice(0, at).toLowerCase();
  if (!ownDomain || at < 1 || email.slice(at + 1).toLowerCase() !== ownDomain.toLowerCase()) {
    return null;
  }
  if (!server || !local) return null;
  const id = `@${local}:${server}`;
  const known = chat
    .rooms()
    .some((room) => chat.roomMembers(room.id).some((m) => m.userId.toLowerCase() === id));
  return known ? id : null;
}

const SLOT_SEARCH_DAYS = 7;
const SLOT_LENGTH = 'PT30M';
const SLOT_TTL_MS = 5 * 60_000;
const SLOT_CACHE_MAX = 200;
type CachedSlot = { at: number; slot: Promise<string | null> };
const slotCache = new WeakMap<CalendarClient, Map<string, CachedSlot>>();

function formatSlot(startIso: string): string {
  const start = new Date(startIso);
  const when = start.toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Free ${when}`;
}

/** The start (ISO) of the next half hour this person is free inside working
 *  hours, or `null` when their calendar is not shared with us -- `findTime`
 *  never counts a principal it could not read as free. Cached per client and
 *  lower-cased email for five minutes, at most 200 entries; a failure is not
 *  cached. */
function nextFreeSlot(
  calendar: CalendarClient,
  email: string,
  principal: () => Promise<string | undefined>,
): Promise<string | null> {
  let byKey = slotCache.get(calendar);
  if (!byKey) {
    byKey = new Map();
    slotCache.set(calendar, byKey);
  }
  const key = email.toLowerCase();
  const hit = byKey.get(key);
  if (hit && Date.now() - hit.at < SLOT_TTL_MS) return hit.slot;
  const cache = byKey;
  const slot = (async () => {
    const id = await principal();
    if (!id) return null;
    const now = new Date();
    const result = await calendar.findTime(
      [id],
      {
        after: now.toISOString(),
        before: new Date(now.getTime() + SLOT_SEARCH_DAYS * 86_400_000).toISOString(),
      },
      SLOT_LENGTH,
      { workingHours: true },
    );
    return result.slots[0]?.after ?? null;
  })().catch(() => {
    if (cache.get(key)?.slot === slot) cache.delete(key);
    return null;
  });
  cache.delete(key);
  cache.set(key, { at: Date.now(), slot });
  while (cache.size > SLOT_CACHE_MAX) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
  return slot;
}

/** What to draw as a person's address when all a caller holds is a chat id.
 *  `@ada:chat.example` is nothing a person can read, and the card prints it
 *  under the name, so it becomes the mail address it stands for: your own, or
 *  the id's local part on the company domain. With neither, the name stands in
 *  and the card shows the name alone. */
function readableAddress(
  address: string,
  name: string | null | undefined,
  people: PeopleSource | null,
): string {
  const match = /^@([^:]+):/.exec(address);
  if (!match?.[1]) return address;
  if (people?.selfAddress && people.chat?.userId() === address) return people.selfAddress;
  if (people?.mailDomain) return `${match[1]}@${people.mailDomain}`;
  return name?.trim() || match[1];
}

const reason = (error: unknown) => (error instanceof Error ? error.message : String(error));

/**
 * The card for one person, resolved from whatever the call site knows: an
 * email address (mail, calendar), a chat user id (chat), or both. `card` is
 * `undefined` outside a `PeopleProvider`, which draws the bare `Identity` as
 * before. Nothing is fetched or subscribed until `active` (the card has been
 * reached by pointer or focus); `dialog` is the "Call <name>?" confirmation.
 */
export function usePersonCard({
  email,
  chatUserId,
  principalId,
  name,
  active,
}: {
  email?: string;
  chatUserId?: string;
  /** A calendar account id, when the caller already holds one. */
  principalId?: string;
  name?: string;
  active: boolean;
}): { card: PersonCardProps | undefined; dialog: ReactNode } {
  const people = useContext(PeopleContext);
  const toast = useToast();
  const chat = people?.chat ?? null;
  const calendar = people?.calendar ?? null;
  const calls = people?.calls;
  const [nextFree, setNextFree] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Resolved against the chat client's rooms as they are now, each render
  // while the card is in use, so a room joined after mount is seen.
  const userId =
    !active || !chat
      ? null
      : (chatUserId ?? (email ? chatUserForEmail(chat, email, people?.mailDomain) : null));
  const self = userId !== null && userId === chat?.userId();

  useEffect(() => {
    setNextFree(null);
    if (!active || !calendar || !email) return;
    let live = true;
    void nextFreeSlot(calendar, email, async () => {
      if (principalId) return principalId;
      return (await calendar.resolvePrincipal(email))?.accountId;
    }).then((slot) => live && setNextFree(slot));
    return () => {
      live = false;
    };
  }, [active, calendar, email, principalId]);

  // Presence is a live read off the chat client's cache, so ask again on change.
  const watching = chat !== null && userId !== null && !self;
  const [, setTick] = useState(0);
  useEffect(
    () => (watching ? chat?.onPresenceChanged(() => setTick((t) => t + 1)) : undefined),
    [watching, chat],
  );

  if (!people) return { card: undefined, dialog: null };
  const state = chat && userId && !self ? presenceState(chat.presence(userId)) : null;
  const directRoom = async (): Promise<string | null> => {
    if (!chat || !userId) return null;
    try {
      return await chat.startDirectMessage([userId]);
    } catch (error) {
      toast(`Could not open a chat: ${reason(error)}`, 'bad');
      return null;
    }
  };
  const call = async () => {
    const roomId = await directRoom();
    if (!roomId || !calls) return;
    // A call that cannot start still lands in the room, whose own call
    // button offers a retry.
    await startOrJoinRoomCall(calls.client, roomId).catch((error: unknown) =>
      toast(`Could not start the call: ${reason(error)}`, 'bad'),
    );
    people.openRoom(roomId);
  };
  const card: PersonCardProps = {};
  if (state) card.presence = state;
  if (nextFree && new Date(nextFree).getTime() > Date.now()) card.nextFree = formatSlot(nextFree);
  if (email) card.onMail = () => people.openMail(email);
  if (chat && userId && !self) {
    card.onMessage = () => {
      void directRoom().then((roomId) => roomId && people.openRoom(roomId));
    };
    if (calls) card.onCall = () => setConfirming(true);
  }
  const label = name?.trim() || email || userId || 'this person';
  const dialog = confirming ? (
    <Dialog
      title={`Call ${label}?`}
      onCancel={() => setConfirming(false)}
      actions={
        <>
          <Button kind="ghost" onPress={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button
            onPress={() => {
              setConfirming(false);
              void call();
            }}
          >
            Call
          </Button>
        </>
      }
    >
      This rings {label} now.
    </Dialog>
  ) : null;
  return { card, dialog };
}

const OPEN_DELAY_MS = 250;
const CLOSE_GRACE_MS = 150;
const CARD_GAP = 6;
const CARD_EDGE = 8;

type Box = { top: number; bottom: number; left: number };

/** Where a card of `size` goes next to `trigger`: under it, or over it when
 *  under would run past `bounds` (the pane the trigger sits in, so a card never
 *  lands on the composer under a stream) and over has room. Never on the
 *  trigger, so the pointer is never under it. */
export function placeCard(
  trigger: Box,
  size: { width: number; height: number },
  bounds: { top: number; bottom: number },
  viewport: { width: number; height: number },
): { top: number; left: number } {
  const below = trigger.bottom + CARD_GAP;
  const above = trigger.top - CARD_GAP - size.height;
  const top = below + size.height <= bounds.bottom || above < bounds.top ? below : above;
  return {
    top: Math.max(CARD_EDGE, Math.min(top, viewport.height - size.height - CARD_EDGE)),
    left: Math.max(CARD_EDGE, Math.min(trigger.left, viewport.width - size.width - CARD_EDGE)),
  };
}

/**
 * The trigger and the card of one person. `@wtfalch/design`'s own card is a
 * modal popover: opening it makes the rest of the page inert, so the pointer
 * stopped being over its trigger, the card closed, the page came back and the
 * card opened again, every 200 ms. This one is not modal: it opens after a short
 * pause on hover, at once on keyboard focus or a press, stays while the pointer
 * or the focus is on the trigger or the card, and sits under the trigger or, at
 * the bottom of a pane, over it.
 */
function PersonCardTrigger({
  label,
  name,
  address,
  card,
  children,
}: {
  label: string;
  name: string | null | undefined;
  address: string;
  card: PersonCardProps;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const root = useRef<HTMLSpanElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const focusCard = useRef(false);
  // Escape hands focus back to the trigger; that focus must not open the card again.
  const skipFocus = useRef(false);
  const trigger = () => root.current?.querySelector<HTMLElement>('button') ?? null;
  const later = (next: boolean, delay: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(next), delay);
  };
  const stay = () => clearTimeout(timer.current);
  const close = () => {
    clearTimeout(timer.current);
    setOpen(false);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const place = () => {
    const button = trigger();
    const box = popup.current;
    if (!button || !box) return;
    setAt(
      placeCard(
        button.getBoundingClientRect(),
        { width: box.offsetWidth, height: box.offsetHeight },
        button.closest('[data-card-bounds]')?.getBoundingClientRect() ?? {
          top: 0,
          bottom: window.innerHeight,
        },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: `place` reads refs only; the card is placed when it opens.
  useLayoutEffect(() => {
    if (!open) {
      setAt(null);
      return;
    }
    place();
    if (focusCard.current) popup.current?.querySelector<HTMLElement>('button')?.focus();
    focusCard.current = false;
  }, [open]);

  // Escape or a press elsewhere puts the card away; a scroll moves it with its trigger.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `close`, `place` and `trigger` read refs and state setters only.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      close();
      skipFocus.current = true;
      trigger()?.focus();
      skipFocus.current = false;
    };
    const onPress = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!popup.current?.contains(target) && !root.current?.contains(target)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPress);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPress);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const act = (run: (() => void) | undefined) => () => {
    close();
    run?.();
  };
  return (
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: hover and focus listeners on a `display: contents` wrapper; the button inside is the control. */}
      <span
        ref={root}
        style={{ display: 'contents' }}
        onPointerEnter={(e) => {
          if (e.pointerType !== 'touch') later(true, OPEN_DELAY_MS);
        }}
        onPointerLeave={() => later(false, CLOSE_GRACE_MS)}
        onFocus={(e) => {
          if (!skipFocus.current && e.target.matches(':focus-visible')) later(true, 0);
        }}
        onBlur={(e) => {
          if (!popup.current?.contains(e.relatedTarget as Node | null))
            later(false, CLOSE_GRACE_MS);
        }}
        onKeyDown={(e) => {
          // The card sits at the end of the page; Tab from its trigger goes into it.
          if (e.key !== 'Tab' || e.shiftKey || !open) return;
          const first = popup.current?.querySelector<HTMLElement>('button');
          if (!first) return;
          e.preventDefault();
          first.focus();
        }}
      >
        <Button
          kind="ghost"
          size="sm"
          className="ident-trigger"
          aria-expanded={open}
          onPress={() => {
            focusCard.current = !open;
            if (open) close();
            else setOpen(true);
          }}
        >
          {children}
        </Button>
      </span>
      {open &&
        createPortal(
          <div
            ref={popup}
            role="dialog"
            aria-label={`${label} — card`}
            className="pop ident-card flex flex-col gap-2 min-w-[14rem] p-3 text-sm text-text"
            style={{
              position: 'fixed',
              top: at?.top ?? 0,
              left: at?.left ?? 0,
              visibility: at ? 'visible' : 'hidden',
            }}
            onPointerEnter={stay}
            onPointerLeave={() => later(false, CLOSE_GRACE_MS)}
            onFocus={stay}
            onBlur={(e) => {
              if (!root.current?.contains(e.relatedTarget as Node | null))
                later(false, CLOSE_GRACE_MS);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Tab' || !e.shiftKey) return;
              const first = popup.current?.querySelector<HTMLElement>('button');
              if (document.activeElement !== first) return;
              e.preventDefault();
              trigger()?.focus();
            }}
          >
            <Identity name={name} address={address} kind="full" />
            {card.presence && <Presence state={card.presence} label={card.presenceLabel} />}
            {card.nextFree && <div className="text-xs text-muted">{card.nextFree}</div>}
            {(card.onMail || card.onMessage || card.onCall) && (
              <div className="flex gap-2">
                {card.onMail && (
                  <Button size="sm" kind="ghost" onPress={act(card.onMail)}>
                    Mail
                  </Button>
                )}
                {card.onMessage && (
                  <Button size="sm" kind="ghost" onPress={act(card.onMessage)}>
                    Message
                  </Button>
                )}
                {card.onCall && (
                  <Button size="sm" kind="ghost" onPress={act(card.onCall)}>
                    Call
                  </Button>
                )}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

/** A chat id that stands for the signed-in person reads as their display name,
 *  then their address -- never the id or its local part. */
function selfName(
  name: string | null | undefined,
  chatUserId: string | undefined,
  people: PeopleSource | null,
): string | null | undefined {
  if (!chatUserId || chatUserId !== people?.chat?.userId()) return name;
  const local = /^@([^:]+):/.exec(chatUserId)?.[1];
  const bare = !name?.trim() || name === local || name === chatUserId;
  return bare ? people.selfName?.trim() || people.selfAddress || name : name;
}

/**
 * `@wtfalch/design`'s `Identity`, with the one shared person card attached.
 * Mail, chat and calendar all draw a name through this, so the card cannot
 * differ between them.
 */
export function PersonIdentity({
  email,
  chatUserId,
  principalId,
  name: given,
  address,
  ...rest
}: Omit<React.ComponentProps<typeof Identity>, 'card'> & {
  email?: string;
  chatUserId?: string;
  principalId?: string;
}) {
  const [active, setActive] = useState(false);
  const people = useContext(PeopleContext);
  const name = selfName(given, chatUserId, people);
  const shown = readableAddress(address, name, people);
  const { card, dialog } = usePersonCard({
    ...(email ? { email } : {}),
    ...(chatUserId ? { chatUserId } : {}),
    ...(principalId ? { principalId } : {}),
    ...(name ? { name } : {}),
    active,
  });
  const reach = () => setActive(true);
  const ident = <Identity name={name} address={shown} {...rest} />;
  return (
    // The card opens on hover or focus; the first of either wakes the lookups.
    <span
      style={{ display: 'contents' }}
      onPointerEnter={reach}
      onFocusCapture={reach}
      onClickCapture={reach}
    >
      {card ? (
        <PersonCardTrigger label={name?.trim() || shown} name={name} address={shown} card={card}>
          {ident}
        </PersonCardTrigger>
      ) : (
        ident
      )}
      {dialog}
    </span>
  );
}

/**
 * `@wtfalch/design`'s `Message`, with the same person card on its author, so a
 * chat message's name opens the card a member row does. `Message` cannot carry
 * this card (its own is the modal one), so the author line is drawn here and
 * `Message` is told it is collapsed; the card names the person, not "You".
 */
export function PersonMessage({
  chatUserId,
  name,
  address,
  discName,
  at,
  guestUntil,
  collapsed,
  className,
  ...rest
}: Omit<React.ComponentProps<typeof Message>, 'authorCard'> & {
  chatUserId: string;
  /** Whose name the card and the disc show, when the printed `name` is not a
   *  name ("You"): the disc stays the person's own. */
  discName?: string;
}) {
  const [active, setActive] = useState(false);
  const people = useContext(PeopleContext);
  const real = selfName(discName ?? name, chatUserId, people);
  const shown = readableAddress(address, real, people);
  const { card, dialog } = usePersonCard({
    chatUserId,
    ...(real ? { name: real } : {}),
    active,
  });
  const { locale } = useDesignLocale();
  const reach = () => setActive(true);
  const when = typeof at === 'string' ? new Date(at) : at;
  const ident = (
    <Identity
      name={name}
      address={shown}
      className="msg-author"
      aside={
        <span className="inline-flex items-center gap-2">
          {guestUntil && <Pill tone="info">Guest · until {guestUntil}</Pill>}
          <time dateTime={when.toISOString()}>
            {new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(when)}
          </time>
        </span>
      }
    />
  );
  return (
    <span
      className={discName ? 'chat-msg-mine' : undefined}
      style={{
        display: 'contents',
        ...(discName
          ? ({
              '--disc-initials': JSON.stringify(initialsOf(real ?? discName, shown)),
            } as CSSProperties)
          : {}),
      }}
      onPointerEnter={reach}
      onFocusCapture={reach}
      onClickCapture={reach}
    >
      {!collapsed && (
        <span className="chat-msg-head">
          {card ? (
            <PersonCardTrigger
              label={real?.trim() || shown}
              name={real}
              address={shown}
              card={card}
            >
              {ident}
            </PersonCardTrigger>
          ) : (
            ident
          )}
        </span>
      )}
      <Message
        name={name}
        address={shown}
        at={at}
        {...rest}
        collapsed
        className={['chat-msg-after-head', className].filter(Boolean).join(' ')}
      />
      {dialog}
    </span>
  );
}
